import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, Checkbox, FormControlLabel, Grid, MenuItem, Chip as MuiChip, TablePagination, TextField, Typography } from '@mui/material'
import { GreenText } from 'components'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { OhioOutgoingTitleDto, OhioOutgoingTitleQueryParams } from 'dtos/ohioOutgoingTitles'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from 'utils'
import OhioOutgoingTitleListCard from './OhioOutgoingTitlesListCard'

const sessionStorageKey = 'ohioOutgoingTitles:queryParams'

const defaultQueryParams: OhioOutgoingTitleQueryParams = {
  includeDeliveredOutgoingTitleGroups: false,
  page: 0,
  pageSize: 10
}

export default function OhioOutgoingTitles() {
  const layout = useLayout()
  const navigate = useNavigate()

  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')

  const [isGettingOhioOutgoingTitleGroups, setIsGettingOhioOutgoingTitleGroups] = useState<boolean>(false)
  const [queryParams, setQueryParams] = useState<OhioOutgoingTitleQueryParams>(
    sessionStorage.getItem(sessionStorageKey) ? JSON.parse(sessionStorage.getItem(sessionStorageKey)!) : defaultQueryParams
  )
  const [ohioOutgoingTitleGroups, setOhioOutgoingTitleGroups] = useState<OhioOutgoingTitleDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const getOhioOutgoingTitleGroups = useDebounce((queryParams: OhioOutgoingTitleQueryParams) => {
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(queryParams))

    setIsGettingOhioOutgoingTitleGroups(true)
    controllers.ohioOutgoingTitles
      .getOhioOutgoingTitleGroups(queryParams)
      .then(response => {
        if ((response.totalCount ?? 0) < queryParams.page * queryParams.pageSize) {
          setQueryParams({ ...queryParams, page: 0 })
        }
        setOhioOutgoingTitleGroups(response.value)
        setTotalCount(response.totalCount!)
      })
      .finally(() => {
        setIsGettingOhioOutgoingTitleGroups(false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    getOhioOutgoingTitleGroups(queryParams)
  }, [getOhioOutgoingTitleGroups, queryParams])

  useEffect(() => {
    layout.setTitle('Ohio Outgoing Titles')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(isGettingOhioOutgoingTitleGroups)
  }, [layout, isGettingOhioOutgoingTitleGroups])

  return (
    <>
      <Grid container spacing={1} alignItems='center'>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container justifyContent='space-between'>
                <Grid item>
                  <Typography fontWeight='bold' variant='h5'>
                    Ohio Outgoing Titles
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
                <Grid item container spacing={1} xs={12} sm={true} alignContent='center'>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      label='Filter Field'
                      onChange={e => {
                        setFilterField(e.target.value)
                        setFilterValue('')
                      }}
                      select
                      size='small'
                      sx={{ minWidth: 200 }}
                      value={filterField || ''}
                    >
                      <MenuItem value='importerStockNumber'>Importer Stock #</MenuItem>

                      <MenuItem value='vin'>VIN</MenuItem>

                      <MenuItem value='exporterCompany'>Exporter</MenuItem>

                      <MenuItem value='buyerCompany'>Buyer</MenuItem>

                      <MenuItem value='titleDestinationCompany'>Title Destination</MenuItem>

                      <MenuItem value='year'>Year</MenuItem>

                      <MenuItem value='make'>Make</MenuItem>

                      <MenuItem value='model'>Model</MenuItem>

                      <MenuItem value='titleUploadedDateTime'>Title Uploaded</MenuItem>

                      <MenuItem value='signedOutboundBOLUploadedDateTime'>Signed BOL</MenuItem>
                    </TextField>
                  </Grid>

                  {['', 'importerStockNumber', 'vin', 'exporterCompany', 'buyerCompany', 'titleDestinationCompany', 'year', 'make', 'model'].includes(
                    filterField
                  ) && (
                    <Grid item xs={12} sm={2}>
                      <TextField
                        disabled={!filterField}
                        label='Filter Value'
                        onChange={e => setFilterValue(e.target.value)}
                        size='small'
                        type={filterField === 'year' ? 'number' : 'text'}
                        value={filterValue || ''}
                      />
                    </Grid>
                  )}

                  {filterField === 'titleUploadedDateTime' && (
                    <>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                          label='Filter Date From'
                          onChange={e => {
                            setFilterValueFrom(e.target.value)
                          }}
                          type='date'
                          value={filterValueFrom}
                        />
                      </Grid>

                      <Grid item xs={12} sm={2}>
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                          label='Filter Date To'
                          onChange={e => {
                            setFilterValueTo(e.target.value)
                          }}
                          type='date'
                          value={filterValueTo}
                        />
                      </Grid>
                    </>
                  )}

                  {filterField === 'signedOutboundBOLUploadedDateTime' && (
                    <>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                          label='Filter Date From'
                          onChange={e => {
                            setFilterValueFrom(e.target.value)
                          }}
                          type='date'
                          value={filterValueFrom}
                        />
                      </Grid>

                      <Grid item xs={12} sm={2}>
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                          label='Filter Date To'
                          onChange={e => {
                            setFilterValueTo(e.target.value)
                          }}
                          type='date'
                          value={filterValueTo}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item>
                    <Button
                      color='inherit'
                      disabled={!filterField || (!filterValue && !(filterValueFrom && filterValueTo))}
                      size='medium'
                      variant='contained'
                      onClick={() => {
                        if (filterField === 'titleUploadedDateTime') {
                          setQueryParams({
                            ...queryParams,
                            titleUploadedDateTimeFrom: filterValueFrom,
                            titleUploadedDateTimeTo: filterValueTo
                          })
                        } else if (filterField === 'signedOutboundBOLUploadedDateTime') {
                          setQueryParams({
                            ...queryParams,
                            signedOutboundBOLUploadedDateTimeFrom: filterValueFrom,
                            signedOutboundBOLUploadedDateTimeTo: filterValueTo
                          })
                        } else {
                          setQueryParams({ ...queryParams, [filterField]: filterValue })
                        }

                        setFilterField('')
                        setFilterValue('')
                        setFilterValueFrom('')
                        setFilterValueTo('')
                      }}
                    >
                      APPLY FILTER
                    </Button>
                  </Grid>

                  <Grid item>
                    <Button
                      color='secondary'
                      disabled={
                        !Boolean(
                          queryParams.importerStockNumber ||
                            queryParams.vin ||
                            queryParams.exporterCompany ||
                            queryParams.buyerCompany ||
                            queryParams.titleDestinationCompany ||
                            queryParams.year ||
                            queryParams.make ||
                            queryParams.model ||
                            (queryParams.titleUploadedDateTimeFrom && queryParams.titleUploadedDateTimeTo) ||
                            (queryParams.signedOutboundBOLUploadedDateTimeFrom && queryParams.signedOutboundBOLUploadedDateTimeTo) ||
                            queryParams.includeDeliveredOutgoingTitleGroups
                        )
                      }
                      size='medium'
                      variant='text'
                      onClick={() => {
                        setQueryParams({
                          ...queryParams,
                          importerStockNumber: undefined,
                          vin: undefined,
                          exporterCompany: undefined,
                          buyerCompany: undefined,
                          titleDestinationCompany: undefined,
                          year: undefined,
                          make: undefined,
                          model: undefined,
                          titleUploadedDateTimeFrom: undefined,
                          titleUploadedDateTimeTo: undefined,
                          signedOutboundBOLUploadedDateTimeFrom: undefined,
                          signedOutboundBOLUploadedDateTimeTo: undefined,
                          includeDeliveredOutgoingTitleGroups: false
                        })
                      }}
                    >
                      RESET FILTERS
                    </Button>
                  </Grid>

                  <Grid item>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={queryParams.includeDeliveredOutgoingTitleGroups}
                          onChange={() =>
                            setQueryParams({ ...queryParams, includeDeliveredOutgoingTitleGroups: !queryParams.includeDeliveredOutgoingTitleGroups })
                          }
                          color='primary'
                        />
                      }
                      label='Include Delivered Outgoing Title Groups'
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} sm='auto' container alignItems='center'>
                  <Grid item>
                    <Button color='primary' onClick={() => navigate('/ohio-outgoing-titles/new')} variant='contained'>
                      ADD GROUP
                    </Button>
                  </Grid>
                </Grid>

                <Grid item xs={12} container spacing={1} alignItems='center'>
                  {queryParams.importerStockNumber && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Importer Stock # contains "${queryParams.importerStockNumber}"`}
                        onDelete={() => setQueryParams({ ...queryParams, importerStockNumber: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.vin && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`VIN contains "${queryParams.vin}"`}
                        onDelete={() => setQueryParams({ ...queryParams, vin: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.exporterCompany && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Exporter contains "${queryParams.exporterCompany}"`}
                        onDelete={() => setQueryParams({ ...queryParams, exporterCompany: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.buyerCompany && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Buyer contains "${queryParams.buyerCompany}"`}
                        onDelete={() => setQueryParams({ ...queryParams, buyerCompany: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.titleDestinationCompany && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Title Destination contains "${queryParams.titleDestinationCompany}"`}
                        onDelete={() => setQueryParams({ ...queryParams, titleDestinationCompany: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.year && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Year contains "${queryParams.year}"`}
                        onDelete={() => setQueryParams({ ...queryParams, year: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.make && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Make contains "${queryParams.make}"`}
                        onDelete={() => setQueryParams({ ...queryParams, make: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.model && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Model contains "${queryParams.model}"`}
                        onDelete={() => setQueryParams({ ...queryParams, model: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.titleUploadedDateTimeFrom && queryParams.titleUploadedDateTimeTo && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Title Uploaded from "${moment(queryParams.titleUploadedDateTimeFrom).format('M/D/yyyy')}" to "${moment(
                          queryParams.titleUploadedDateTimeTo
                        ).format('M/D/yyyy')}"`}
                        onDelete={() => setQueryParams({ ...queryParams, titleUploadedDateTimeFrom: undefined, titleUploadedDateTimeTo: undefined })}
                        variant='outlined'
                      />
                    </Grid>
                  )}

                  {queryParams.signedOutboundBOLUploadedDateTimeFrom && queryParams.signedOutboundBOLUploadedDateTimeTo && (
                    <Grid item xs={12} sm='auto'>
                      <MuiChip
                        color='secondary'
                        label={`Signed BOL Date from "${moment(queryParams.signedOutboundBOLUploadedDateTimeFrom).format('M/D/yyyy')}" to "${moment(
                          queryParams.signedOutboundBOLUploadedDateTimeTo
                        ).format('M/D/yyyy')}"`}
                        onDelete={() =>
                          setQueryParams({ ...queryParams, signedOutboundBOLUploadedDateTimeFrom: undefined, signedOutboundBOLUploadedDateTimeTo: undefined })
                        }
                        variant='outlined'
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {ohioOutgoingTitleGroups.map(ohioOutgoingTitleGroup => (
          <Grid item xs={12} key={ohioOutgoingTitleGroup.ohioOutgoingTitleGuid}>
            <OhioOutgoingTitleListCard ohioOutgoingTitle={ohioOutgoingTitleGroup}>
              <Grid item xs={12} sm='auto'>
                <Button
                  endIcon={<Edit />}
                  onClick={() => {
                    navigate(`/ohio-outgoing-titles/${ohioOutgoingTitleGroup.ohioOutgoingTitleGuid}`)
                  }}
                >
                  MANAGE GROUP
                </Button>
              </Grid>
            </OhioOutgoingTitleListCard>
          </Grid>
        ))}

        <Grid item xs={12}>
          <GreenText>
            {`**Ohio Outgoing Titles:**

The Ohio Outgoing Titles screen lists all groups of outgoing titles. Each card represents a group, displaying all vehicles associated with it.

   - Cards are sorted by “Group Shipped” date, newest to oldest.
   - Groups without a "Group Shipped" date are listed last.
   - A group is considered delivered when the "Group Delivered" date is populated.

**Filter Field Drop-Down:**

Clicking the drop-down displays a list of columns available for filtering:

   - Importer Stock #
   - VIN
   - Exporter
   - Buyer
   - Title Destination
   - Year
   - Make
   - Model
   - Title Uploaded
   - Signed BOL

**Filter Value:**

The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":
   - Importer Stock#: Displays a free-type filter field.
   - VIN: Displays a free-type filter field.
   - Exporter: Displays a free-type filter field.
   - Buyer: Displays a free-type filter field.
   - Title Destination: Displays a free-type filter field.
   - Year: Displays a free-type filter field.
   - Make: Displays a free-type filter field.
   - Model: Displays a free-type filter field.
   - Title Uploaded: Displays a free-type filter field.
   - Signed BOL: Displays a free-type filter field.

**Apply Filter Button:**

Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.

**Include Delivered Outgoing Title Groups Check Box:**

By default, this option is unchecked. When checked, completed Delivered Outgoing Title Groups are displayed.

**Add Group Button:**

Clicking this button opens an empty Ohio Outgoing Titles – Group Details page.

**Group Locked Chip:**

This chip is displayed when a group is marked as locked in the Ohio Outgoing Titles – Details page.

**Manage Group Button:**

Clicking this button navigates the user to Ohio Outgoing Titles – Group Details for the selected group.

**Importer Stock#:**

Displays a sequential number starting at 20000, followed by a three-character Exporter Code or Buyer Code (if the vehicle has a buyer). Populated only when the vehicle is locked.

**VIN Column:**

Displays the VIN for the vehicle from the Vehicle Details.

**Exporter Column:**

Displays the Exporter for the vehicle.

**Buyer Column:**

Displays the buyer for the vehicle.

If there is no buyer, the row displays "-".

**Title Destination:**

Displays the Title Destination Company name for the vehicle.

**Year Column:**

Displays the vehicle’s year from Vehicle Details.

**Make Column:**

Displays the vehicle’s make from Vehicle Details.

**Model Column:**

Displays the vehicle’s model from Vehicle Details.

**Title Upload Date:**

Displays the date the title was uploaded for the vehicle.

**Signed BOL:**

Displays the date the signed Bill of Lading (BOL) was uploaded for the vehicle.

**Count:**

Displays the total number of vehicles in the group.

**Tracking #:**

Reads from Ohio Outgoing Titles – Group Details.

This is the FedEx tracking number for the Outgoing Titles Group. The tracking number is a clickable link that opens the FedEx website.

**Group Shipped:**

Reads from Ohio Outgoing Titles – Group Details.

Indicates that the FedEx API reports the group is in FedEx possession.

If no value exists, a "-" is displayed.

**Group Delivered:**

Reads from Ohio Outgoing Titles – Group Details.

Indicates that the FedEx API reports a delivered date for this group’s tracking number.

If no value exists, a "-" is displayed.

**Created:**

Displays the date and user who created the Group.

**Modified:**

Displays the date and user who most recently modified the Group.`}
          </GreenText>
        </Grid>

        <Grid item xs={12}>
          <TablePagination
            component='div'
            count={totalCount}
            onPageChange={(_, page: number) => setQueryParams({ ...queryParams, page })}
            onRowsPerPageChange={e => setQueryParams({ ...queryParams, pageSize: e.target.value as unknown as number })}
            page={queryParams.page}
            rowsPerPage={queryParams.pageSize}
            rowsPerPageOptions={[
              { label: '5', value: 5 },
              { label: '10', value: 10 },
              { label: '25', value: 25 },
              { label: '50', value: 50 },
              { label: '100', value: 100 }
            ]}
            SelectProps={{ fullWidth: false }}
          />
        </Grid>
      </Grid>
    </>
  )
}
